import { Controller } from "@hotwired/stimulus"
import { MediaFormMixin } from "./mixins/media_form_mixin"

/**
 * Post Form Controller
 * Handles post creation/editing with media uploads, tagged friends, and visibility
 */
export default class extends MediaFormMixin(Controller) {
  static targets = [
    "form",
    "mediaPreview",
    "mediaIdsContainer",
    "mediaInput"
  ]

  connect() {
    this.selectedFiles = []
    this.isUploading = false
    this.setupWebcamListener()
  }

  uploadMedia(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return
    
    // Add files to selected files array
    files.forEach(file => {
      this.selectedFiles.push(file)
      this.addLocalPreview(file)
    })
    
    // Reset file input
    event.target.value = ''
  }
  
  addLocalPreview(file) {
    const fileId = `file_${Date.now()}_${Math.random()}`
    const { element: preview } = this.buildLocalPreviewElement(file, {
      removeAction: "post-form#removeLocalMedia",
      fileId,
      size: "md"
    })
    preview.dataset.fileIndex = this.selectedFiles.length - 1
    this.mediaPreviewTarget.appendChild(preview)
    this.updateMediaPreviewVisibility()
  }
  
  removeLocalMedia(event) {
    const fileId = event.currentTarget.dataset.fileId
    const preview = this.mediaPreviewTarget.querySelector(`[data-file-id="${fileId}"]`)
    
    if (preview) {
      const fileIndex = parseInt(preview.dataset.fileIndex)
      
      // Revoke object URL to free memory
      if (preview.dataset.objectUrl) {
        URL.revokeObjectURL(preview.dataset.objectUrl)
      }
      
      // Remove file from array
      this.selectedFiles.splice(fileIndex, 1)
      
      // Remove preview element
      preview.remove()
      
      // Update remaining file indices
      this.mediaPreviewTarget.querySelectorAll('[data-file-index]').forEach((el, index) => {
        el.dataset.fileIndex = index
      })

      this.updateMediaPreviewVisibility()
    }
  }

  removeExistingMedia(event) {
    const mediaId = event.currentTarget.dataset.mediaId
    const preview = this.mediaPreviewTarget.querySelector(`[data-media-id="${mediaId}"]`)
    
    if (preview) {
      // Remove the preview element (including the hidden input)
      preview.remove()
      
      // Add a hidden field to mark this media for deletion
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'remove_media_ids[]'
      input.value = mediaId
      this.mediaIdsContainerTarget.appendChild(input)
    }

    this.updateMediaPreviewVisibility()
  }
  
  // This method is called when form is submitted
  async handleSubmit(event) {
    // If already uploading, let the form submit normally
    if (this.isUploading) {
      return
    }

    // If there are files to upload, prevent default submit and upload first
    if (this.selectedFiles.length > 0) {
      event.preventDefault()

      this.isUploading = true

      try {
        const uploadedMediaIds = await this.uploadMediaFiles(this.selectedFiles)
        
        // Add media IDs to form (append directly to form to ensure Turbo picks them up)
        uploadedMediaIds.forEach(id => {
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = 'media_ids[]'
          input.value = id
          this.formTarget.appendChild(input)
        })
        
        // Clear selected files since they're now uploaded
        this.selectedFiles = []
        
        // Now submit the form
        this.formTarget.requestSubmit()
        
      } catch (error) {
        console.error('Upload error:', error)
        alert('Upload error. Please check your connection.')
        this.isUploading = false

        // Ask any submit-with-spinner button to reset its UI
        this.element.dispatchEvent(
          new CustomEvent("submit-with-spinner:reset", { bubbles: true })
        )
      }
    }
    // If there are no files to upload, we just let the form submit normally
  }

  handleWebcamCaptured(event) {
    const { file } = event.detail
    if (file) {
      // Add the captured file to selectedFiles like a regular file upload
      this.selectedFiles.push(file)
      this.addLocalPreview(file)
    }
  }

  disconnect() {
    this.revokeObjectUrls()
    this.disconnectWebcamListener()
  }
}

